import { Box, Flex, Heading, Text } from '@chakra-ui/react'
import { FC, useCallback, useMemo, useState } from 'react'

import Layout from '@component/Layout'
import ArticleDetail from '@component/Pages/HelpCenter/ArticleDetail'
import ArticleList from '@component/Pages/HelpCenter/ArticleList'
import CategoryFilter from '@component/Pages/HelpCenter/CategoryFilter'
// import Chatbot from '@component/Pages/HelpCenter/Chatbot'
import SearchBar from '@component/Pages/HelpCenter/SearchBar'

import articles, {
  Article,
  ArticleCategory
} from '@base/variables/help-center/articles'

function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

const HelpCenter: FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<
    ArticleCategory[]
  >([])
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const filteredArticles = useMemo(() => {
    let result = articles

    if (selectedCategories.length > 0) {
      result = result.filter(a => selectedCategories.includes(a.category))
    }

    if (searchQuery.trim()) {
      const query = normalize(searchQuery)
      result = result.filter(a => {
        const searchable = normalize(
          `${a.title} ${a.summary} ${a.tags.join(' ')}`
        )
        return searchable.includes(query)
      })
    }

    return result
  }, [searchQuery, selectedCategories])

  const handleSelectArticle = useCallback((article: Article) => {
    setSelectedArticle(article)
    setIsDrawerOpen(true)
  }, [])

  const handleArticleFromChat = useCallback(
    (articleId: string) => {
      const article = articles.find(a => a.id === articleId)
      if (article) handleSelectArticle(article)
    },
    [handleSelectArticle]
  )

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query)
  }, [])

  return (
    <Layout title="Central de Ajuda">
      <Box px={[4, 6, 8]} py={6} maxW="1400px" mx="auto">
        <Box mb={6}>
          <Heading fontSize="2xl" fontWeight="700" color="gray.800">
            Central de Ajuda
          </Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Encontre tutoriais, artigos e tire suas dúvidas com o assistente
            virtual.
          </Text>
        </Box>

        <Flex direction="column" mb={6}>
          <Box mb={4}>
            <SearchBar onSearch={handleSearch} />
          </Box>
          <CategoryFilter
            selected={selectedCategories}
            onChange={setSelectedCategories}
          />
        </Flex>

        <Box>
          <Text fontSize="sm" color="gray.500" mb={3}>
            {filteredArticles.length} artigo
            {filteredArticles.length !== 1 ? 's' : ''} encontrado
            {filteredArticles.length !== 1 ? 's' : ''}
          </Text>
          <ArticleList
            articles={filteredArticles}
            onSelect={handleSelectArticle}
          />
        </Box>

        {/* Chat desabilitado — manter para reativar depois */}
        {/* <Box pos="sticky" top="80px">
          <Chatbot onArticleClick={handleArticleFromChat} />
        </Box> */}
      </Box>

      <ArticleDetail
        article={selectedArticle}
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onNavigate={handleArticleFromChat}
      />
    </Layout>
  )
}

export default HelpCenter
